/**
 * Session Service for PKCE OAuth2 Flow
 *
 * This service handles JWT-based authentication with the ZITADEL backend,
 * including authorization code exchange, token storage, and user data mapping.
 * Permissions and admin status are derived from JWT groups claim, not from callback user.roles/user.permissions.
 */

import { tokenStore } from '@peritasai/data-access';
import { Permission, User, UserProfile } from '../../../shared/auth';
import {
  apiClient,
  LogoutResponse,
  TokenExchangeRequest,
  TokenExchangeResponse,
} from './api.client';
import {
  createSessionKey,
  decodeAccessTokenPayload,
  decodeIdTokenPayload,
  getInactivityLimitMs,
  getIssuedAt,
  getPermissionsFromGroups,
  getStableSessionId,
  isAdminFromGroups,
} from './jwt-utils';

/** Result of logout - redirected=true when navigating to Zitadel end_session */
export interface LogoutResult {
  redirected?: boolean;
}

/** Options for logout - onRedirectFailure called if redirect to Zitadel never happens */
export interface SessionLogoutOptions {
  onRedirectFailure?: () => void;
}

const REDIRECT_FAILURE_TIMEOUT_MS = 2500;
const AUTOLOGOUT_TRACE_PREFIX = '[AUTOLOGOUT-TRACE][session-service]';

function logAutologoutTrace(
  message: string,
  details?: Record<string, unknown>
): void {
  if (typeof window === 'undefined') {
    return;
  }

  console.log(AUTOLOGOUT_TRACE_PREFIX, message, details ?? {});
}

/**
 * Session Service
 *
 * Handles the complete PKCE session-based authentication flow:
 * 1. Exchange authorization code for session
 * 2. Manage user sessions
 * 3. Map backend user data to internal types
 * 4. Derive permissions from JWT groups (not from callback user.roles)
 * 5. Handle session validation and logout
 */
export class SessionService {
  /**
   * Exchange authorization code for session
   * This is called after the user returns from ZITADEL authorization
   */
  async exchangeCodeForSession(
    code: string,
    codeVerifier: string,
    redirectUri: string
  ): Promise<{
    user: User;
    userProfile: UserProfile;
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  }> {
    try {
      const request: TokenExchangeRequest = {
        code,
        codeVerifier,
        redirectUri,
      };

      const response: TokenExchangeResponse = await apiClient.exchangeTokens(
        request
      );

      if (!response.success) {
        throw new Error('Session exchange failed');
      }

      if (!response.tokenResponse) {
        throw new Error('Backend response missing tokenResponse');
      }

      const tokenResponse = response.tokenResponse as {
        access_token?: string;
        refresh_token?: string;
        expires_in?: number | string;
        accessToken?: string;
        refreshToken?: string;
        expiresIn?: number | string;
        id_token?: string;
      };

      const accessToken =
        tokenResponse.access_token ?? tokenResponse.accessToken ?? null;
      const refreshToken =
        tokenResponse.refresh_token ?? tokenResponse.refreshToken ?? null;
      const idToken = tokenResponse.id_token ?? null;
      const expiresInRaw =
        tokenResponse.expires_in ?? tokenResponse.expiresIn ?? null;
      const expiresIn =
        expiresInRaw !== null ? Number(expiresInRaw) : Number.NaN;

      if (!accessToken || !refreshToken || !Number.isFinite(expiresIn)) {
        throw new Error(
          'Backend tokenResponse missing required fields (access_token, refresh_token, expires_in)'
        );
      }

      // Store tokens in TokenStore for Bearer auth
      tokenStore.setTokens(accessToken, refreshToken, expiresIn, idToken);

      const accessPayload = decodeAccessTokenPayload(accessToken);
      const idPayload = idToken ? decodeIdTokenPayload(idToken) : null;
      const userId = this.resolveTokenUserId(accessPayload, idPayload);
      const sessionMetadata = this.buildSessionMetadata(accessToken, userId);

      const user = this.mapTokensToUser(
        accessPayload,
        idPayload,
        sessionMetadata
      );
      const userProfile = this.mapTokensToUserProfile(
        accessPayload,
        idPayload,
        sessionMetadata
      );

      return {
        user,
        userProfile,
        accessToken,
        refreshToken,
        expiresIn,
      };
    } catch (error) {
      throw new Error(
        `Failed to exchange authorization code: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  /**
   * Get session from JWT tokens (access token + id_token).
   * No /me call - session derived from tokens. Triggers refresh if needed.
   */
  async getSessionFromTokens(): Promise<{
    user: User;
    userProfile: UserProfile;
  } | null> {
    try {
      const accessToken = await tokenStore.getAccessTokenOrRefresh();
      if (!accessToken) return null;

      const accessPayload = decodeAccessTokenPayload(accessToken);
      const idToken = tokenStore.getIdToken();
      const idPayload = idToken ? decodeIdTokenPayload(idToken) : null;
      const userId = this.resolveTokenUserId(accessPayload, idPayload);
      const sessionMetadata = this.buildSessionMetadata(accessToken, userId);

      const user = this.mapTokensToUser(
        accessPayload,
        idPayload,
        sessionMetadata
      );
      const userProfile = this.mapTokensToUserProfile(
        accessPayload,
        idPayload,
        sessionMetadata
      );

      return { user, userProfile };
    } catch {
      return null;
    }
  }

  /**
   * Logout user and clear session.
   * Bearer token is added by api client interceptor.
   * Returns { redirected: true } when navigating to Zitadel end_session - caller should not update state.
   * If redirect fails (e.g. user closes tab, network error), onRedirectFailure is called after timeout.
   */
  async logout(options?: SessionLogoutOptions): Promise<LogoutResult> {
    try {
      const response: LogoutResponse = await apiClient.logout();

      if (!response.success) {
        throw new Error('Logout failed');
      }

      tokenStore.clear();

      if (response.logoutUrl) {
        window.location.href = response.logoutUrl;
        // Fallback: if redirect never happens (user closes tab, network error), run cleanup
        setTimeout(() => {
          options?.onRedirectFailure?.();
        }, REDIRECT_FAILURE_TIMEOUT_MS);
        return { redirected: true };
      }

      return { redirected: false };
    } catch (err) {
      tokenStore.clear();
      throw new Error(
        `Failed to logout: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`
      );
    }
  }

  /**
   * Refresh access token using refresh token.
   */
  async refreshAccessToken(): Promise<string | null> {
    const refreshToken = tokenStore.getRefreshToken();
    if (!refreshToken) return null;

    try {
      logAutologoutTrace('refresh-access-token:start', {
        hasRefreshToken: true,
      });
      const tokenResponse = await apiClient.refreshTokens(refreshToken);
      tokenStore.setTokens(
        tokenResponse.access_token,
        tokenResponse.refresh_token,
        tokenResponse.expires_in,
        tokenResponse.id_token
      );
      const accessPayload = decodeAccessTokenPayload(tokenResponse.access_token);
      logAutologoutTrace('refresh-access-token:success', {
        sub: accessPayload?.sub ?? null,
        iat: accessPayload?.iat ?? null,
        sid: getStableSessionId(accessPayload),
        inactivityLimitMs: getInactivityLimitMs(accessPayload),
        derivedSessionKey: createSessionKey(
          accessPayload?.sub ?? '',
          getStableSessionId(accessPayload),
          getIssuedAt(accessPayload)
        ),
      });
      return tokenResponse.access_token;
    } catch {
      logAutologoutTrace('refresh-access-token:error');
      tokenStore.clear();
      return null;
    }
  }

  private mapTokensToUser(
    accessPayload: ReturnType<typeof decodeAccessTokenPayload>,
    idPayload: ReturnType<typeof decodeIdTokenPayload>,
    authData: {
      permissions: Permission[];
      isAdmin: boolean;
      inactivityLimitMs: number | null;
      sessionKey: string | null;
    }
  ): User {
    const sub = this.resolveTokenUserId(accessPayload, idPayload);
    const username =
      idPayload?.preferred_username ?? idPayload?.email ?? (sub || 'unknown');

    return {
      id: sub,
      username,
      permissions: authData.permissions,
      isAdmin: authData.isAdmin,
      isActive: true,
      inactivityLimitMs: authData.inactivityLimitMs,
      sessionKey: authData.sessionKey,
    };
  }

  private mapTokensToUserProfile(
    accessPayload: ReturnType<typeof decodeAccessTokenPayload>,
    idPayload: ReturnType<typeof decodeIdTokenPayload>,
    authData: {
      permissions: Permission[];
      isAdmin: boolean;
      inactivityLimitMs: number | null;
      sessionKey: string | null;
    }
  ): UserProfile {
    const user = this.mapTokensToUser(accessPayload, idPayload, authData);
    const iat =
      idPayload?.iat ?? accessPayload?.iat ?? Math.floor(Date.now() / 1000);
    const ts = new Date(iat * 1000);

    return {
      ...user,
      email: idPayload?.email ?? '',
      firstName: idPayload?.given_name ?? '',
      lastName: idPayload?.family_name ?? '',
      createdAt: ts,
      updatedAt: ts,
      lastLogin: ts,
    };
  }

  private buildSessionMetadata(accessToken: string, userId: string) {
    const payload = decodeAccessTokenPayload(accessToken);
    const groups = payload?.groups ?? [];

    return {
      permissions: getPermissionsFromGroups(groups),
      isAdmin: isAdminFromGroups(groups),
      inactivityLimitMs: getInactivityLimitMs(payload),
      sessionKey: createSessionKey(
        userId,
        getStableSessionId(payload),
        getIssuedAt(payload)
      ),
    };
  }

  private resolveTokenUserId(
    accessPayload: ReturnType<typeof decodeAccessTokenPayload>,
    idPayload: ReturnType<typeof decodeIdTokenPayload>
  ): string {
    return accessPayload?.sub ?? idPayload?.sub ?? '';
  }
}

export const sessionService = new SessionService();
